import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { LineChart as MuiLineChart } from '@mui/x-charts/LineChart';
import { MakeOptional } from '@mui/x-charts/internals';
import {
  AxisConfig,
  ChartsXAxisProps,
  ChartsYAxisProps,
  LineSeriesType,
  ScaleName
} from '@mui/x-charts';
import {
  VDR_SERIES_COLORS,
  vdrChartSx,
  vdrLegendLabelStyle,
  vdrOverlaySlotProps,
  vdrSeriesDashSx,
  vdrTooltipPopperSx
} from './vdrChartStyle';

// Series sin significado propio: paleta de marca en orden fijo (y trazo discontinuo desde la
// 4.ª serie). Las páginas que pasan `colors` (deciles, bandas, MT/BT…) conservan los suyos.
const DEFAULT_LINE_COLORS = VDR_SERIES_COLORS;
const SX_PALETA_PROPIA = vdrChartSx;
const SX_PALETA_MARCA = { ...vdrChartSx, ...vdrSeriesDashSx };

/** Misma regla que `defaultizeAxis` en MUI X Charts (id por defecto si no viene en el objeto). */
function resolvedYAxisId(
  axis: MakeOptional<AxisConfig<ScaleName, any, ChartsYAxisProps>, 'id'> | undefined,
  index: number
): string {
  if (axis && typeof axis.id === 'string' && axis.id.length > 0) {
    return axis.id;
  }
  return `defaultized-y-axis-${index}`;
}

const LineChart: FunctionComponent<{
  xAxis: MakeOptional<AxisConfig<ScaleName, any, ChartsXAxisProps>, 'id'>[];
  yAxis: MakeOptional<AxisConfig<ScaleName, any, ChartsYAxisProps>, 'id'>[];
  series: MakeOptional<LineSeriesType, 'type'>[];
  height: number;
  /** Paleta opcional (p. ej. una línea por decil); si no se pasa se usan los colores por defecto. */
  colors?: string[];
}> = ({ xAxis, yAxis, series, height, colors }) => {
  const [renderTrigger, setRenderTrigger] = useState<number>(0);
  const [width, setWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  /* 
  NOTE: The line chart has a 'bug'. When the container has already been rendered and its width is enlarged, the points and lines are rescaled, but some of them 'disappear'.
  - Wrap the line graph in a new container and listen to its width so that a trigger by means of the key re-render the graph for each change of width.
  - This problem occurs when the line graph is inside a component that changes width. For example when there is a sideBar and it is hidden.
  */

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    updateWidth();

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, []);

  const forceRerender = () => {
    setRenderTrigger((prev) => prev + 1);
  };

  useEffect(() => forceRerender(), [width]);

  // Sin marcas por defecto (evita círculos vacíos en cada punto). El hover sigue mostrando
  // el punto resaltado relleno vía LineHighlightPlot (fill del color de la serie).
  const seriesResolved = series.map((s) => ({
    ...s,
    showMark: s.showMark ?? false
  }));

  // Margen inferior: ticks + etiqueta del eje X + leyenda (fila o dos filas si hay muchas series).
  // Valores bajos hacen que la leyenda se superponga al nombre del eje (p. ej. gráficos con 2 series).
  const marginBottom = seriesResolved.length > 5 ? 148 : 112;

  // MUI LineChart por defecto solo pinta `leftAxis` (primer eje Y); `rightAxis` es null salvo que se indique.
  const hasDualYAxis = Array.isArray(yAxis) && yAxis.length >= 2;
  const leftAxisId = hasDualYAxis ? resolvedYAxisId(yAxis[0], 0) : undefined;
  const rightAxisId = hasDualYAxis ? resolvedYAxisId(yAxis[1], 1) : undefined;
  const primaryYAxisId = yAxis?.length ? resolvedYAxisId(yAxis[0], 0) : 'defaultized-y-axis-0';

  /** Separa el título del eje Y de los números de los ticks (MUI coloca la etiqueta muy cerca). */
  const yAxisLabelSlot = (x: number) => ({
    slotProps: { axisLabel: { x } }
  });

  const leftAxisResolved =
    hasDualYAxis && leftAxisId && rightAxisId
      ? { axisId: leftAxisId, ...yAxisLabelSlot(-58) }
      : { axisId: primaryYAxisId, ...yAxisLabelSlot(-52) };

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <MuiLineChart
        key={renderTrigger}
        xAxis={xAxis}
        yAxis={yAxis}
        series={seriesResolved}
        colors={colors && colors.length > 0 ? colors : DEFAULT_LINE_COLORS}
        grid={{ vertical: true, horizontal: true }}
        height={height}
        sx={colors && colors.length > 0 ? SX_PALETA_PROPIA : SX_PALETA_MARCA}
        leftAxis={leftAxisResolved}
        {...(hasDualYAxis && leftAxisId && rightAxisId
          ? { rightAxis: { axisId: rightAxisId, ...yAxisLabelSlot(60) } }
          : {})}
        margin={{
          top: 16,
          bottom: marginBottom,
          left: hasDualYAxis ? 72 : 62,
          right: hasDualYAxis ? 72 : 28
        }}
        slotProps={{
          legend: {
            direction: 'row',
            position: { vertical: 'bottom', horizontal: 'middle' },
            padding: { top: 4, bottom: 10, left: 8, right: 8 },
            itemGap: 10,
            markGap: 6,
            itemMarkWidth: 12,
            itemMarkHeight: 12,
            labelStyle: { ...vdrLegendLabelStyle, fontSize: 12 }
          },
          popper: { sx: vdrTooltipPopperSx },
          ...vdrOverlaySlotProps
        }}
      />
    </div>
  );
};

export default LineChart;
